import React, { useState, useEffect, useRef } from "react";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";

const ORDER_TYPES = ["Market", "Limit", "Stop-Loss"];

const OrderPanel = ({ stock, defaultSide = "buy", onClose, onSubmit, playerCash = 0 }) => {
  const [side, setSide] = useState(defaultSide);
  const [orderType, setOrderType] = useState("Market");
  const [quantity, setQuantity] = useState("10");
  const [limitPrice, setLimitPrice] = useState("");
  const [stopPrice, setStopPrice] = useState("");
  const [useMargin, setUseMargin] = useState(false);
  const [leverage, setLeverage] = useState(1);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const quantityRef = useRef(null);
  const limitPriceRef = useRef(null);
  const submitRef = useRef(null);

  useEffect(() => {
    if (stock) {
      setLimitPrice(stock?.price?.toFixed(2));
      setStopPrice((stock?.price * 0.95)?.toFixed(2));
    }
  }, [stock]);

  useEffect(() => { setSide(defaultSide); }, [defaultSide]);

  if (!stock) return (
    <div className="flex flex-col items-center justify-center h-48 text-muted-foreground gap-2 p-4" role="status" aria-label="No stock selected">
      <Icon name="ShoppingCart" size={32} color="var(--color-muted-foreground)" />
      <span className="font-caption text-sm text-center">Select a stock to place an order</span>
    </div>
  );

  const execPrice = orderType === "Market" ? stock?.price : parseFloat(limitPrice) || stock?.price;
  const qty = parseInt(quantity) || 0;
  const baseCost = execPrice * qty;
  const marginCost = useMargin ? baseCost / leverage : baseCost;
  const fee = baseCost * 0.001;
  const total = marginCost + fee;

  const validate = () => {
    const errs = {};
    const qtyNum = parseInt(quantity);
    if (!quantity || isNaN(qtyNum) || qtyNum <= 0 || !Number.isInteger(qtyNum)) {
      errs.quantity = 'Quantity must be a positive integer';
    } else if (qtyNum < 1 || qtyNum > 1000000) {
      errs.quantity = 'Quantity must be between 1 and 1,000,000';
    }
    if (orderType === 'Limit') {
      const lp = parseFloat(limitPrice);
      if (!limitPrice || isNaN(lp) || lp <= 0) {
        errs.limitPrice = 'Price must be positive with max 2 decimals';
      } else {
        const decimals = (limitPrice?.split('.')?.[1] || '')?.length;
        if (decimals > 2) errs.limitPrice = 'Price must be positive with max 2 decimals';
      }
    }
    if (!orderType) {
      errs.orderType = 'Please select an order type';
    }
    if (!stock?.ticker) {
      errs.stock = 'Please select a stock';
    }
    if (side === 'buy' && playerCash > 0 && total > playerCash) {
      const needed = (total - playerCash)?.toFixed(2);
      errs.funds = `Insufficient funds — you need $${parseFloat(needed)?.toLocaleString('en-US', { minimumFractionDigits: 2 })} more`;
    }
    return errs;
  };

  const handleQuantityKeyDown = (e) => {
    if (e?.key === 'Enter') {
      e?.preventDefault();
      if (orderType === 'Limit') {
        limitPriceRef?.current?.focus();
      } else {
        submitRef?.current?.focus();
      }
    }
  };

  const handleLimitPriceKeyDown = (e) => {
    if (e?.key === 'Enter') {
      e?.preventDefault();
      submitRef?.current?.focus();
    }
  };

  const handleSubmit = () => {
    const errs = validate();
    if (Object.keys(errs)?.length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    if (!showConfirm) { setShowConfirm(true); return; }
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setShowConfirm(false);
      onSubmit && onSubmit({ stock, side, orderType, quantity: qty, price: execPrice, useMargin, leverage });
      onClose && onClose();
    }, 1500);
  };

  const hasErrors = Object.keys(errors)?.length > 0;

  return (
    <div className="p-4 space-y-4" role="form" aria-label="Place stock order">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon name="ShoppingCart" size={16} color="var(--color-primary)" />
          <span className="font-heading text-sm font-semibold text-foreground">Place Order</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded flex items-center justify-center text-xs font-heading font-bold"
            style={{ background: stock?.color + "22", color: stock?.color }}>
            {stock?.ticker?.slice(0, 2)}
          </div>
          <span className="font-heading text-sm font-bold text-foreground">{stock?.ticker}</span>
          <span className="font-data text-sm text-muted-foreground" aria-label={`Current price $${stock?.price?.toFixed(2)}`}>${stock?.price?.toFixed(2)}</span>
        </div>
      </div>
      {/* Buy/Sell Toggle */}
      <div className="grid grid-cols-2 gap-1 p-1 bg-secondary rounded-lg" role="group" aria-label="Order side">
        {["buy", "sell"]?.map(s => (
          <button key={s} onClick={() => setSide(s)}
            aria-pressed={side === s}
            aria-label={`${s === 'buy' ? 'Buy' : 'Sell'} order`}
            className={`py-2 rounded-md font-heading text-sm font-semibold transition-all duration-150 capitalize ${side === s ? (s === "buy" ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40" : "bg-red-500/20 text-red-400 border border-red-500/40") : "text-muted-foreground hover:text-foreground"}`}>
            {s}
          </button>
        ))}
      </div>
      {/* Order Type */}
      <div>
        <label className="font-caption text-xs text-muted-foreground uppercase tracking-wider mb-2 block" id="order-type-label">Order Type</label>
        <div className="flex gap-2" role="group" aria-labelledby="order-type-label">
          {ORDER_TYPES?.map(t => (
            <button key={t} onClick={() => setOrderType(t)}
              aria-pressed={orderType === t}
              aria-label={`${t} order type`}
              className={`flex-1 py-1.5 rounded-lg font-caption text-xs font-medium transition-all duration-150 ${orderType === t ? "bg-primary/20 text-primary border border-primary/40" : "bg-secondary text-muted-foreground border border-border hover:border-primary/30"}`}>
              {t}
            </button>
          ))}
        </div>
      </div>
      {/* Quantity */}
      <div>
        <label htmlFor="order-quantity" className="font-caption text-xs text-muted-foreground uppercase tracking-wider mb-2 block">Quantity (Shares)</label>
        <div className="flex items-center gap-2">
          <button onClick={() => { setQuantity(q => String(Math.max(1, parseInt(q) - 1))); setErrors(e => ({ ...e, quantity: '' })); }}
            aria-label="Decrease quantity"
            className="w-8 h-8 rounded-lg bg-secondary border border-border flex items-center justify-center hover:border-primary/40 transition-all flex-shrink-0">
            <Icon name="Minus" size={14} color="var(--color-muted-foreground)" />
          </button>
          <input
            id="order-quantity"
            ref={quantityRef}
            type="number"
            value={quantity}
            onChange={e => { setQuantity(e?.target?.value); setErrors(prev => ({ ...prev, quantity: '', funds: '' })); }}
            onKeyDown={handleQuantityKeyDown}
            min="1"
            aria-label="Trade quantity"
            aria-invalid={!!errors?.quantity}
            aria-describedby={errors?.quantity ? 'quantity-error' : undefined}
            className={`flex-1 text-center py-2 bg-secondary border rounded-lg font-data text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-colors ${errors?.quantity ? 'border-error' : 'border-border focus:border-primary/50'}`} />
          <button onClick={() => { setQuantity(q => String(parseInt(q) + 1)); setErrors(e => ({ ...e, quantity: '' })); }}
            aria-label="Increase quantity"
            className="w-8 h-8 rounded-lg bg-secondary border border-border flex items-center justify-center hover:border-primary/40 transition-all flex-shrink-0">
            <Icon name="Plus" size={14} color="var(--color-muted-foreground)" />
          </button>
        </div>
        {errors?.quantity && <p id="quantity-error" className="mt-1 text-xs text-error flex items-center gap-1" role="alert"><Icon name="AlertCircle" size={11} />{errors?.quantity}</p>}
      </div>
      {/* Limit Price */}
      {orderType === "Limit" && (
        <div>
          <label htmlFor="limit-price" className="font-caption text-xs text-muted-foreground uppercase tracking-wider mb-2 block">Limit Price ($)</label>
          <input
            id="limit-price"
            ref={limitPriceRef}
            type="number"
            value={limitPrice}
            onChange={e => { setLimitPrice(e?.target?.value); setErrors(prev => ({ ...prev, limitPrice: '', funds: '' })); }}
            onKeyDown={handleLimitPriceKeyDown}
            step="0.01"
            aria-label="Limit order price"
            aria-invalid={!!errors?.limitPrice}
            aria-describedby={errors?.limitPrice ? 'limit-price-error' : undefined}
            className={`w-full py-2 px-3 bg-secondary border rounded-lg font-data text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-colors ${errors?.limitPrice ? 'border-error' : 'border-border focus:border-primary/50'}`} />
          {errors?.limitPrice && <p id="limit-price-error" className="mt-1 text-xs text-error flex items-center gap-1" role="alert"><Icon name="AlertCircle" size={11} />{errors?.limitPrice}</p>}
        </div>
      )}
      {/* Stop Price */}
      {orderType === "Stop-Loss" && (
        <div>
          <label htmlFor="stop-price" className="font-caption text-xs text-muted-foreground uppercase tracking-wider mb-2 block">Stop Price ($)</label>
          <input
            id="stop-price"
            type="number"
            value={stopPrice}
            onChange={e => setStopPrice(e?.target?.value)}
            step="0.01"
            aria-label="Stop loss price"
            className="w-full py-2 px-3 bg-secondary border border-border rounded-lg font-data text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-colors" />
        </div>
      )}
      {/* Margin Trading */}
      <div className="p-3 rounded-lg border border-border bg-secondary/50">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Icon name="Layers" size={14} color="var(--color-accent)" />
            <span className="font-caption text-xs text-foreground font-medium">Margin Trading</span>
          </div>
          <button
            onClick={() => setUseMargin(m => !m)}
            role="switch"
            aria-checked={useMargin}
            aria-label="Toggle margin trading"
            className={`w-10 h-5 rounded-full transition-all duration-200 relative focus:outline-none focus:ring-2 focus:ring-primary/50 ${useMargin ? "bg-primary" : "bg-muted"}`}>
            <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all duration-200 ${useMargin ? "left-5" : "left-0.5"}`} />
          </button>
        </div>
        {useMargin && (
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="font-caption text-xs text-muted-foreground">Leverage: {leverage}x</span>
              <span className="font-caption text-xs text-accent">Max 5x</span>
            </div>
            <input
              type="range" min="1" max="5" step="1" value={leverage}
              onChange={e => setLeverage(parseInt(e?.target?.value))}
              aria-label={`Leverage ${leverage}x`}
              className="w-full accent-primary" />
            <div className="flex justify-between mt-1">
              {[1, 2, 3, 4, 5]?.map(l => (
                <span key={l} className={`font-caption text-xs ${leverage === l ? "text-primary" : "text-muted-foreground"}`}>{l}x</span>
              ))}
            </div>
          </div>
        )}
      </div>
      {/* Cost Summary */}
      <div className="p-3 rounded-lg border border-border bg-card space-y-1.5" aria-label="Order cost summary">
        <div className="flex justify-between">
          <span className="font-caption text-xs text-muted-foreground">Est. Cost</span>
          <span className="font-data text-xs text-foreground">${baseCost?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </div>
        {useMargin && (
          <div className="flex justify-between">
            <span className="font-caption text-xs text-muted-foreground">Margin Required</span>
            <span className="font-data text-xs text-accent">${marginCost?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
        )}
        <div className="flex justify-between">
          <span className="font-caption text-xs text-muted-foreground">Fee (0.1%)</span>
          <span className="font-data text-xs text-muted-foreground">${fee?.toFixed(2)}</span>
        </div>
        <div className="flex justify-between border-t border-border pt-1.5">
          <span className="font-caption text-xs font-semibold text-foreground">Total</span>
          <span className="font-data text-sm font-bold text-primary">${total?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </div>
      </div>
      {/* Validation Errors */}
      {errors?.funds && (
        <div className="p-2 rounded-lg bg-error/10 border border-error/30 flex items-center gap-2" role="alert">
          <Icon name="AlertCircle" size={13} color="var(--color-error)" />
          <span className="text-xs text-error">{errors?.funds}</span>
        </div>
      )}
      {/* Confirm Warning */}
      {showConfirm && !submitted && (
        <div className="p-3 rounded-lg border border-amber-500/40 bg-amber-500/10 flex items-start gap-2" role="alert">
          <Icon name="AlertTriangle" size={14} color="#f59e0b" className="flex-shrink-0 mt-0.5" />
          <span className="font-body text-xs text-amber-400">
            Confirm {side?.toUpperCase()} {qty} shares of {stock?.ticker} at ${execPrice?.toFixed(2)}?
          </span>
        </div>
      )}
      {/* Submit */}
      <Button
        ref={submitRef}
        variant={side === "buy" ? "success" : "danger"}
        fullWidth
        loading={submitted}
        disabled={hasErrors && !showConfirm}
        onClick={handleSubmit}
        aria-label={submitted ? 'Processing order' : showConfirm ? `Confirm ${side} order` : `Place ${side} order for ${stock?.ticker}`}
        iconName={submitted ? undefined : side === "buy" ? "TrendingUp" : "TrendingDown"}
        iconPosition="left">
        {submitted ? "Processing..." : showConfirm ? "Confirm Order" : `${side === "buy" ? "Buy" : "Sell"} ${stock?.ticker}`}
      </Button>
      {showConfirm && !submitted && (
        <Button variant="outline" fullWidth onClick={() => setShowConfirm(false)} aria-label="Cancel order confirmation">Cancel</Button>
      )}
    </div>
  );
};

export default OrderPanel;